import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BaseReservation } from "@r6digital/rapidstor-types/types/Reservation";
import {
  RapidStorMerchandise,
  RapidStorResponse,
  RapidStorUnitList,
} from "@r6digital/rapidstor-types/types/Api";
import Charge from "@r6digital/rapidstor-types/types/Charge";
import {
  options,
  RapidStorChargesMerchRequest,
  RapidStorCostRetrieve,
  RapidStorInsurance,
  RapidStorLocation,
  RapidStorMoveInResponse,
  RapidStorReservation,
  RapidStorReservationRequest,
  RapidStorScaMoveIn,
  RapidStorTrackEvent,
  RapidStorTrackEventRequest,
} from "../../services/rapidstor";
import store, { RootState } from "../../store";
import Location, { locationTransformer } from "./entities/Location";
import { createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import { EntityState } from "@reduxjs/toolkit/src/entities/models";
import Descriptor, { descriptorTransformer, UnitList } from "./entities/Descriptor";
import { unitTypeTransformer } from "./entities/UnitType";
import defaultLocations from "./defaultLocations";
import Insurance, { insuranceTransformer, InsuranceList } from "./entities/Insurance";

const locationsAdapter = createEntityAdapter<Location>({
  selectId: (location) => location.locationCode,
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});
const locationInitialState = locationsAdapter.getInitialState({
  ids: [],
  entities: defaultLocations.map((location) => locationTransformer(location)),
});

const baseQuery = fetchBaseQuery({
  baseUrl: options.baseURL,
});

const CorpCode = "CSBT";

export const rapidstorApi = createApi({
  reducerPath: "rapidstorApi",
  baseQuery: baseQuery,
  keepUnusedDataFor: 3600,
  tagTypes: ["Location", "Questionnaire", "UnitList", "Reservation"],
  endpoints: (builder) => ({
    getLocations: builder.query<EntityState<Location>, void>({
      query: () => ({
        url: "/api/v1/locations",
        headers: {
          corp: CorpCode,
        },
      }),
      providesTags: ["Location"],
      transformResponse: (resp: RapidStorResponse<RapidStorLocation[]>) => {
        const locations = resp.results
          .map((location) => locationTransformer(location))
          .filter((location) => location.rapidstorEnabled)
          .filter((location) => !options.hiddenLocations.includes(location.locationCode));

        return locationsAdapter.setAll(locationInitialState, locations);
      },
    }),
    getUnitList: builder.query<UnitList, string>({
      query: (locationCode) => ({
        url: `/api/v1/locations/${locationCode}/unit-list`,
        headers: {
          corp: CorpCode,
          site: locationCode,
        },
      }),
      providesTags: (result) => {
        return result ? [{ type: "UnitList", id: result.unitTypes[0].locationCode }] : [];
      },
      transformResponse: (resp: RapidStorResponse<RapidStorUnitList>): UnitList => {
        return {
          descriptors: resp.results.descriptors
            .map((d): Descriptor => {
              const location: Location | undefined = selectLocationByCode(
                store.getState(),
                d.sLocationCode
              );
              return descriptorTransformer(d, location);
            })
            .sort((a, b) => a.ordinalPosition - b.ordinalPosition)
            .filter((d) => d.unitTypes.length > 0 && !d.hidden && d.enabled)
            .concat([
              {
                _id: "",
                locationCode: resp.results.descriptors[0].sLocationCode,
                corpCode: resp.results.descriptors[0].sCorpCode,
                name: "250+",
                description:
                  "We offer large spaces suitable for commercial use, offices, distribution centres and much more. Chat to our team about your situation and let’s find you the perfect space.",
                descriptions: [
                  "Larger spaces to suit many commercial uses",
                  "Flexible leases",
                  "Parking, Wi-Fi, Amenities & Bills all included",
                ],
                picture: "https://ik.imagekit.io/bytcx9plm/3x7.5_i_o.png",
                slides: ["APEX", "HEM", "MOL", "WAL", "WEL"].includes(
                  resp.results.descriptors[0].sLocationCode
                )
                  ? [
                      "https://ik.imagekit.io/bytcx9plm/CSBT-APEX-descriptor-picture-clndvizsg000a01loc7mz3wtu.png",
                      "https://ik.imagekit.io/bytcx9plm/CSBT-APEX-descriptor-slide-clndvj1y7000b01loc95phh6z.png",
                      "https://ik.imagekit.io/bytcx9plm/CSBT-APEX-descriptor-slide-clndvj4ip000c01lo0qvf0ltx.png",
                    ]
                  : [
                      "https://ik.imagekit.io/bytcx9plm/CSBT-ASH-descriptor-slide-clm7f6o1e000801pjgfue28wy.png",
                      "https://ik.imagekit.io/bytcx9plm/CSBT-LTRAIN-descriptor-slide-clmqxdehg000b01qjd3a841ub.png",
                      "https://ik.imagekit.io/bytcx9plm/CSBT-LTRAIN-descriptor-slide-clmqxdhu0000c01qj9zoe91eu.png",
                    ],
                defaultInsuranceCoverage: null,
                deals: [],
                enabled: true,
                hidden: false,
                unitTypes: [],
                ordinalPosition: 999,
                area: "250+",
              },
            ]),
          unitTypes: resp.results.unitTypes.map((u) => {
            const location: Location | undefined = selectLocationByCode(
              store.getState(),
              u.sLocationCode
            );
            return unitTypeTransformer(u, location);
          }),
        };
      },
    }),
    getInsuranceCoverage: builder.query<InsuranceList, string>({
      query: (locationCode) => ({
        url: `/api/v1/${locationCode}/insurance`,
        headers: {
          corp: CorpCode,
          site: locationCode,
        },
      }),
      transformResponse: (
        resp: RapidStorResponse<RapidStorInsurance[]>
      ): InsuranceList => {
        return {
          insuranceTypes: resp.results.map((i): Insurance => {
            return insuranceTransformer(i);
          }),
        };
      },
    }),
    getReservation: builder.query({
      query: ({ locationCode, token }) => ({
        url: `/api/v1/reservation/${token}`,
        method: "GET",
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<RapidStorReservation>) => {
        return resp.results;
      },
      providesTags: ["Reservation"],
    }),
    getMerchandise: builder.query({
      query: (locationCode) => ({
        url: `/api/v1/merchandise`,
        method: "GET",
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<RapidStorMerchandise>) => {
        return resp.results;
      },
    }),
    getCharges: builder.query({
      query: (locationCode) => ({
        url: `/api/v1/locations/${locationCode}/charges`,
        method: "GET",
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<Charge[]>) => {
        return resp.results;
      },
    }),
    postReservation: builder.mutation<BaseReservation, RapidStorReservationRequest>({
      query: ({ locationCode, payload }) => ({
        url: `/api/v1/reservation`,
        method: "POST",
        body: payload,
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<BaseReservation>) => {
        return resp.results;
      },
    }),
    patchReservation: builder.mutation({
      query: ({ locationCode, payload, token }) => ({
        url: `/api/v1/reservations/${token}`,
        method: "PATCH",
        body: payload,
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<BaseReservation>) => {
        return resp.results;
      },
    }),
    postMoveIn: builder.mutation({
      query: ({ locationCode, payload, token }) => ({
        url: `/api/v1/move-in/${token}`,
        method: "POST",
        body: payload,
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<RapidStorMoveInResponse>) => {
        return resp.results;
      },
    }),
    getMoveInCostRetrieve: builder.query({
      query: ({ location, reservationId }) => ({
        url: `/api/v1/move-in/${reservationId}/cost-retrieve`,
        method: "GET",
        headers: {
          site: location.locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<RapidStorCostRetrieve>) => {
        return resp.results;
      },
    }),
    postScaMoveIn: builder.mutation({
      query: ({ locationCode, payload }) => ({
        url: `/api/v1/sca/prepare/move-in`,
        method: "POST",
        body: payload,
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<RapidStorScaMoveIn>) => {
        return resp.results;
      },
    }),
    postScaMoveInBillingInfoUpdate: builder.mutation({
      query: ({ locationCode, payload }) => ({
        url: `/api/v1/sca/prepare/move-in/billing-info-update`,
        method: "POST",
        body: payload,
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<RapidStorScaMoveIn>) => {
        return resp.results;
      },
    }),
    postScaMoveInPayment: builder.mutation({
      query: ({ locationCode, payload }) => ({
        url: `/api/v1/sca/prepare/move-in/payment`,
        method: "POST",
        body: payload,
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<RapidStorScaMoveIn>) => {
        return resp.results;
      },
    }),
    postScaPayment: builder.mutation({
      query: ({ locationCode, payload }) => ({
        url: `/api/v1/sca/prepare/payment`,
        method: "POST",
        body: payload,
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<RapidStorScaMoveIn>) => {
        return resp.results;
      },
    }),
    postSaveQuote: builder.mutation<BaseReservation, RapidStorReservationRequest>({
      query: ({ locationCode, payload }) => ({
        url: `/api/v1/reservation/save-quote`,
        method: "POST",
        body: payload,
        headers: {
          site: locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<BaseReservation>) => {
        return resp.results;
      },
    }),
    postChargesMerch: builder.mutation<BaseReservation, RapidStorChargesMerchRequest>({
      query: (request) => ({
        url: `/api/v1/tenant/${request.tenantId}/ledgers/${request.ledgerId}/charges-merch`,
        method: "POST",
        body: request.payload,
        headers: {
          site: request.locationCode,
          corp: CorpCode,
        },
      }),
      transformResponse: (resp: RapidStorResponse<BaseReservation>) => {
        return resp.results;
      },
    }),
    postTrackEvent: builder.mutation<RapidStorTrackEvent, RapidStorTrackEventRequest>({
      query: ({ corpToken, locationToken, payload }) => ({
        url: `/rapidstor/${corpToken}/${locationToken}/api/track`,
        method: "POST",
        body: payload,
      }),
    }),
  }),
});

const selectLocationsResult = rapidstorApi.endpoints.getLocations.select();
const selectLocationData = createSelector(selectLocationsResult, (locationResult) => {
  if (locationResult.status !== "fulfilled") {
    return locationsAdapter.setAll(
      locationInitialState,
      defaultLocations.map((l) => locationTransformer(l))
    );
  }
  return locationResult.data;
});

export const { selectAll: selectAllLocations, selectById: selectLocationByCode } =
  locationsAdapter.getSelectors(
    (state: RootState) => selectLocationData(state) ?? locationInitialState
  );

export const selectLocationsByState = createSelector(
  [selectAllLocations, (state, region?: string) => region],
  (locations, region) =>
    locations.filter((location) => (region ? location.address.region === region : true))
);

export const selectLocationBySlug = createSelector(
  [selectAllLocations, (state, slug: string) => slug],
  (locations, slug) => locations.find((location) => location.slug === slug)
);

export const {
  useGetLocationsQuery,
  useGetUnitListQuery,
  useGetChargesQuery,
  useGetMerchandiseQuery,
  usePostReservationMutation,
  usePatchReservationMutation,
  usePostSaveQuoteMutation,
  usePostMoveInMutation,
  usePostScaMoveInMutation,
  useGetInsuranceCoverageQuery,
  useGetReservationQuery,
  useGetMoveInCostRetrieveQuery,
  usePostScaMoveInBillingInfoUpdateMutation,
  usePostScaMoveInPaymentMutation,
  usePostChargesMerchMutation,
  usePostScaPaymentMutation,
  usePostTrackEventMutation,
} = rapidstorApi;
